import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {MAX_WEEKDAY, OperationModeInterval} from './object-week-filter.model';
import {SemRestRequestService} from '../../../projects/api/src/sem-rest-request/sem-rest-request.service';
import {ApiList} from '../API/api-list.enum';
import {TranslateService} from '@ngx-translate/core';
import {LocaleService} from '../shared/services/locale.service';
import {formatNumberToTime, numToShortWeekday, parseIntervalToMinutes, shortWeekdayToNum} from '../shared/pipes/time.pipe';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfigureService} from '../../../projects/common/modules/configure/configure.service';
import {DateTimeAdapter, OwlDateTimeIntl} from '@danielmoncada/angular-datetime-picker';

/**
 * Корневой компонент для управления календарями выбранных объектов
 * @param objectIds - выбранные объекты для указания календарей
 */
@Component({
  selector: 'app-object-scheduler',
  templateUrl: './object-scheduler.component.html',
  styleUrls: ['./object-scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectSchedulerComponent implements OnInit {

  @Input() ids: any;
  @Input() names: any[];
  @Input() saveComplete;
  @Output() cancel = new EventEmitter();
  controllerIds = [];
  isRouteToControllers = false;
  isApplyScheduleToController = false;
  public operationIntervals: OperationModeInterval[] = [{
    days: {
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false,
      d1: false,
      d2: false
    },
    start: '08:00',
    end: '20:00'
  }];

  public showModalDialog = false;
  public modalDialogTemplate;
  isAdmin = false;

  constructor(private request: SemRestRequestService,
              private translate: TranslateService,
              private owlDateTimeIntl: OwlDateTimeIntl,
              private localeService: LocaleService,
              private dateTimeAdapter: DateTimeAdapter<Date>,
              private router: Router,
              private route: ActivatedRoute,
              private cd: ChangeDetectorRef,
              private config: ConfigureService
  ) {
    localeService.locale$.subscribe(locale => {
      dateTimeAdapter.setLocale(locale.serverShort);
    });
    this.isAdmin = config.options.appName === 'admin';
  }

  ngOnInit() {
    if (this.ids.length === 1) {
      this.request
        .baseUrlAppend(ApiList.Db, ApiList.Object, this.ids[0], ApiList.Operatingmode)
        .getAll<any>().subscribe(this.parse.bind(this));

      this.request
        .baseUrlAppend(ApiList.Db, ApiList.Controller, ApiList.Child, ApiList.Object, this.ids[0])
        .getAll<any>().subscribe(data =>  {
          if (data && data.length > 0) {
            this.controllerIds = data.map(controller => controller.id);
            if (this.controllerIds.length === 1) {
              this.isApplyScheduleToController = true;
              this.isRouteToControllers = false;
            }
            if (this.controllerIds.length > 1) {
              this.isRouteToControllers = true;
              this.isApplyScheduleToController = false;
            }

          }
      });
    }
  }

  /** Разбирает данные загруженные с сервера */
  public parse(data) {
    const newIntervals: OperationModeInterval[] = [];

    const createOrAppendInterval = (_newInterval) => {
      if (
        (formatNumberToTime(_newInterval.workingstart) === '00:00'
          && formatNumberToTime(_newInterval.workingstop) === '00:00'
        )
        || _newInterval.weekday > MAX_WEEKDAY) {
        return;
      }

      let tmpInterval: OperationModeInterval = newIntervals.find(_operationInterval =>
        formatNumberToTime(_operationInterval.start) === formatNumberToTime(_newInterval.workingstart)
        && formatNumberToTime(_operationInterval.end) === formatNumberToTime(_newInterval.workingstop));
      if (!tmpInterval) {
        tmpInterval = {
          days: {
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false,
            d1: false,
            d2: false
          },
          start: formatNumberToTime(_newInterval.workingstart),
          end: formatNumberToTime(_newInterval.workingstop)
        };
        newIntervals.push(tmpInterval);
      }
      tmpInterval.days[numToShortWeekday(_newInterval.weekday)] = true;
    };
    data.forEach(createOrAppendInterval);
    this.operationIntervals = newIntervals;
    this.cd.markForCheck();
  }

  /**
   * Преобразовывает интервал для передачи на сервер
   */
  public formatOperationIntervalScheduler() {
    const week: { [k: string]: { workingstart: string; workingstop: string } } = {
      mon: {workingstart: '00:00', workingstop: '00:00'},
      tue: {workingstart: '00:00', workingstop: '00:00'},
      wed: {workingstart: '00:00', workingstop: '00:00'},
      thu: {workingstart: '00:00', workingstop: '00:00'},
      fri: {workingstart: '00:00', workingstop: '00:00'},
      sat: {workingstart: '00:00', workingstop: '00:00'},
      sun: {workingstart: '00:00', workingstop: '00:00'},
      d1: {workingstart: '00:00', workingstop: '00:00'},
      d2: {workingstart: '00:00', workingstop: '00:00'}
    };

    Object.keys(week).forEach(weekday => {
      week[weekday] = this.operationIntervals.reduce((result, _interval) => _interval.days[weekday] ?
        {workingstart: _interval.start, workingstop: _interval.end} : result, week[weekday]);
    });

    return Object.keys(week).map(weekday => ({...week[weekday], weekday: weekday}));
  }

  /**
   * Добавляет еще один интервал для режима работы
   */
  public addOperationModeInterval() {
    this.operationIntervals.push({
      days: {
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false,
        d1: false,
        d2: false
      },
      start: '08:00',
      end: '20:00'
    });
  }

  /**
   * Убирает интервал режима работы
   * @param id - числовой индекс в массиве интервалов
   */
  removeOperationModeInterval(id: number) {
    this.operationIntervals = this.operationIntervals.filter((el, idx) => idx !== id);
  }

  /**
   * Формирует список режимов работы и отправляет запрос на сервер для сохранения режима работы
   */
  createOperationModeInterval() {

    const dataIntervals = this.formatOperationIntervalScheduler().map(interval => ({
      weekday: shortWeekdayToNum(interval.weekday),
      workingstart: parseIntervalToMinutes(interval.workingstart + ''),
      workingstop: parseIntervalToMinutes(interval.workingstop + '')
    }));
    this.request
      .baseUrlAppend(ApiList.Db, ApiList.Object, ApiList.Operatingmode, ApiList.Update)
      .baseUrlAppendByStrParam('?id=' + this.ids.join('&id='))
      .postData(dataIntervals)
      .subscribe(() => {
        if (this.isApplyScheduleToController && this.isAdmin) {
          this.request.baseUrlAppend(ApiList.Db, ApiList.Controller, this.controllerIds[0])
            .baseUrlAppendByStrParam('/deploy_schedule')
            .getSingle().subscribe(() => this.saveComplete(), () => this.saveComplete());

        } else {
          this.saveComplete();
          if (this.isRouteToControllers) {
            this.router.navigate(['main', 'page', 'object', this.ids[0], 'controller']);
          }
        }

      });


    this.hideDialog();
  }


  /**
   * Устанавливает шаблон для модального окна и отображает модальное окно
   * @param dialogTemplate - cсылка на шаблон отображения в модальном окне
   */
  confirmSetAction(dialogTemplate) {
    this.modalDialogTemplate = dialogTemplate;
    this.showModalDialog = true;
  }

  /**
   * Скрывает диалог
   */
  hideDialog() {
    this.showModalDialog = false;
  }

  /**
   * Возвращает текущую локаль
   * @returns {string}
   */
  public locale() {
    return this.translate.currentLang;
  }
}
